<section class="training-page">
  @if (!isLoading && !errorMessage && !isFinished && currentCard) {
    <div class="training-accent-band" aria-hidden="true"></div>
  }

  <div class="main-content">
    @if (isLoading) {
      <div class="training-status alert alert-info py-2" role="status">
        {{ t()["train.loading"] }}
      </div>
    }

    @if (errorMessage) {
      <div class="training-status alert alert-danger py-2" role="alert">
        {{ errorMessage }}
      </div>
    }

    @if (!isLoading && !errorMessage && flashcards.length === 0) {
      <div
        class="training-status alert alert-warning py-2 text-center"
        data-testid="training-empty-state"
        role="status"
      >
        <div>{{ emptyMessage }}</div>
        <div class="d-flex justify-content-center gap-2 flex-wrap mt-3">
          <button
            type="button"
            class="btn btn-outline-secondary training-empty-back"
            (click)="back()"
          >
            {{ t()["train.back"] }}
          </button>
          @if (canTrainAllCardsFromEmptyState) {
            <button
              type="button"
              class="btn btn-primary training-empty-train-all"
              data-testid="training-empty-train-all"
              (click)="trainAllCards()"
            >
              {{ t()["train.emptyDueTrainAll"] }}
            </button>
          }
        </div>
      </div>
    }

    @if (!isLoading && !errorMessage && isFinished) {
      <div class="card training-finished-card" data-testid="training-finished">
        <div class="card-body training-finished-body">
          <div class="training-finished-header">
            <div class="training-finished-icon" aria-hidden="true">
              <svg class="sa-icon sa-bold sa-nofill">
                <use href="/assets/icons/sprite.svg#check-circle"></use>
              </svg>
            </div>

            <div class="training-finished-heading">
              <h4 class="training-finished-title mb-0">
                {{ finishedTrainingI18n().title }}
              </h4>
              <p class="training-finished-description text-body-secondary mb-0">
                {{ finishedTrainingI18n().description }}
              </p>
            </div>

            <div class="training-finished-actions">
              <button
                type="button"
                class="btn btn-outline-secondary"
                (click)="back()"
              >
                {{ finishedTrainingI18n().back }}
              </button>
              <button type="button" class="btn btn-primary" (click)="restart()">
                {{ finishedTrainingI18n().restart }}
              </button>
            </div>
          </div>

          <div
            class="training-finished-summary"
            [attr.aria-label]="finishedTrainingI18n().summaryTitle"
          >
            <div class="training-finished-summary-header">
              <div>
                <div class="training-finished-summary-title">
                  {{ finishedTrainingI18n().summaryTitle }}
                </div>
                <div class="training-finished-summary-subtitle">
                  {{ finishedTrainingI18n().summarySubtitle }}
                </div>
              </div>
              <div class="training-finished-total">
                <div class="training-finished-total-value">
                  {{ finishedReviewedCardsCount() }}
                </div>
                <div class="training-finished-total-label">
                  {{ finishedTrainingI18n().reviewedCards }}
                </div>
              </div>
            </div>

            <div class="training-finished-stats-grid">
              @for (stat of finishedReviewStats(); track stat.grade) {
                <div
                  class="training-finished-stat training-finished-stat--{{
                    stat.tone
                  }}"
                >
                  <div class="training-finished-stat-count">
                    {{ stat.count }}
                  </div>
                  <div class="training-finished-stat-label">
                    {{ t()[stat.labelKey] }}
                  </div>
                </div>
              }
            </div>

            @if (isPracticeMode) {
              <div class="training-practice-notice" role="status">
                <span class="training-practice-notice-icon" aria-hidden="true">
                  <svg class="sa-icon sa-bold sa-nofill">
                    <use href="/assets/icons/sprite.svg#check-circle"></use>
                  </svg>
                </span>
                <span class="training-practice-notice-copy">
                  {{ t()["train.practiceNotice"] }}
                </span>
              </div>
            }

            @if (!isPracticeMode && showDailyGoalProgress()) {
              <div class="training-finished-progress" role="status">
                <span
                  class="training-finished-progress-icon"
                  aria-hidden="true"
                >
                  <svg class="sa-icon sa-bold sa-nofill">
                    <use href="/assets/icons/sprite.svg#target"></use>
                  </svg>
                </span>

                <div class="training-finished-progress-content">
                  <div class="training-finished-progress-title">
                    {{ dailyGoalProgressTitle() }}
                  </div>
                  <div class="training-finished-progress-summary">
                    {{ dailyGoalProgressSummary() }}
                  </div>
                  <app-train-daily-goal-progress
                    class="training-finished-progress-bar"
                    [reviewedCards]="dailyGoalReviewedCards()"
                    [dailyCardsTarget]="dailyGoalCardsTarget()"
                    [ariaLabel]="dailyGoalProgressAriaLabel()"
                    [tooltip]="dailyGoalProgressTooltip()"
                  />
                </div>
              </div>
            }
          </div>
        </div>
      </div>
    }

    @if (!isLoading && !errorMessage && !isFinished && currentCard; as card) {
      @if (isSupportedCard(card)) {
        <app-train-card-shell
          [deckTitle]="displayDeckTitle"
          [card]="card"
          [isPracticeMode]="isPracticeMode"
          [shortTalkSessionId]="shortTalkSessionIdForCache"
          [position]="currentIndex + 1"
          [isBackVisible]="isAnswerVisible"
          [isTypedOrVoiceCorrect]="typedOrVoiceIsCorrect"
          [isTypedOrVoiceSelfAssessment]="isTypedOrVoiceSelfAssessment(card)"
          [typedOrVoiceUserAnswer]="typedOrVoiceUserAnswer"
          [isMultipleChoiceCorrect]="multipleChoiceIsCorrect"
          [multipleChoiceSelectedOptionId]="multipleChoiceSelectedOptionId"
          [multipleChoiceSelectedOptionIds]="multipleChoiceSelectedOptionIds"
          [multipleChoicePlayableOptionIds]="
            resolveMultipleChoiceOptionPlayableIds(card)
          "
          [isBusy]="isReviewBusy"
          [imageAsset]="resolveImageAsset(card)"
          [audioAsset]="resolveAudioAsset(card)"
          [videoAsset]="resolveVideoAsset(card)"
          [canSpeakQuestion]="canSpeakQuestion(card)"
          [questionAudioUrl]="resolveQuestionAudioUrl(card)"
          [playingAudioUrl]="playingAudioUrl"
          [showTrainingSessionProgress]="showTrainingSessionProgress()"
          [trainingSessionReviewedCards]="trainingSessionReviewedCardsCount()"
          [trainingSessionCardsTarget]="trainingSessionCardsTarget()"
          [trainingSessionProgressLabel]="trainingSessionProgressAriaLabel()"
          [selectedReviewGrade]="selectedReviewGrade"
          [shortTalkOpeningReplyRequestId]="shortTalkOpeningReplyRequestId()"
          (continue)="continueCurrentCard()"
          (close)="back()"
          (speak)="speakCurrentCard()"
          (shortTalkIntroStarted)="onShortTalkIntroStarted()"
          (shortTalkMessagesChange)="onShortTalkMessagesChange($event)"
          (pronunciationAssessmentComplete)="
            onPronunciationAssessmentComplete()
          "
          (userAnswerChange)="onTypedOrVoiceUserAnswerChange($event)"
          (playAudio)="playCurrentAudioAsset()"
          (stopPlayback)="stopPlayback()"
          (reviewGradeChange)="submitFeedbackReview($event)"
          (selectedOptionIdChange)="
            onMultipleChoiceSelectedOptionChange($event)
          "
          (selectedOptionIdsChange)="
            onMultipleChoiceSelectedOptionIdsChange($event)
          "
          (optionSpeak)="playMultipleChoiceOption($event)"
        />
      } @else {
        <div class="training-fallback-stage">
          <h3 class="deck-title text-center mb-3">{{ displayDeckTitle }}</h3>

          <div class="card training-fallback-card">
            <div class="card-body training-shell">
              <div
                class="d-flex flex-nowrap align-items-start gap-3 mb-4 w-100"
              >
                <button
                  type="button"
                  class="btn btn-icon btn-outline-primary rounded-circle waves-effect flex-shrink-0 training-back-btn"
                  [attr.aria-label]="t()['train.back']"
                  [title]="t()['train.back']"
                  (click)="back()"
                >
                  <svg class="sa-icon sa-icon-2x sa-bold sa-nofill">
                    <use href="/assets/icons/sprite.svg#book-open"></use>
                  </svg>
                </button>

                <div class="text-end ms-auto flex-grow-1">
                  <div
                    class="d-flex align-items-center justify-content-end gap-2"
                  >
                    <div ngbDropdown class="training-review-grade">
                      <button
                        type="button"
                        class="btn btn-icon btn-system training-review-grade-toggle"
                        ngbDropdownToggle
                        [disabled]="isReviewBusy"
                        [attr.aria-label]="
                          reviewGradeButtonLabel(selectedReviewGrade)
                        "
                        [title]="reviewGradeButtonLabel(selectedReviewGrade)"
                      >
                        <svg
                          class="sa-icon sa-bold sa-nofill"
                          aria-hidden="true"
                        >
                          <use href="/assets/icons/sprite.svg#zap"></use>
                        </svg>
                      </button>
                      <div ngbDropdownMenu class="dropdown-menu-end">
                        @for (
                          reviewGrade of reviewGradeOptions;
                          track reviewGrade
                        ) {
                          <button
                            type="button"
                            ngbDropdownItem
                            [class.active]="selectedReviewGrade === reviewGrade"
                            [disabled]="isReviewBusy"
                            (click)="onReviewGradeChange(reviewGrade)"
                          >
                            {{ reviewGradeLabel(reviewGrade) }}
                          </button>
                        }
                      </div>
                    </div>
                    <span class="text-body-secondary">#{{ card.id }}</span>
                  </div>
                </div>
              </div>

              <app-fallback-train-card
                [card]="card"
                [isAnswerVisible]="isAnswerVisible"
                [sessionProgressLabel]="sessionProgressLabel"
                (flip)="onCardInteraction($event)"
              />
            </div>
          </div>
        </div>

        <div class="training-nav-actions">
          <button
            type="button"
            class="btn btn-outline-primary"
            [disabled]="isReviewBusy"
            (click)="finish()"
          >
            {{ t()["train.finish"] }}
          </button>

          <div class="d-flex align-items-center training-nav-buttons">
            <button
              type="button"
              class="btn btn-icon btn-outline-primary rounded-circle waves-effect training-nav-btn"
              [attr.aria-label]="t()['train.previous']"
              [title]="t()['train.previous']"
              [disabled]="!canGoPrevious || isReviewBusy"
              (click)="previous()"
            >
              <svg class="sa-icon sa-bold sa-nofill">
                <use href="/assets/icons/sprite.svg#arrow-left"></use>
              </svg>
            </button>

            <button
              type="button"
              class="btn btn-icon btn-outline-primary rounded-circle waves-effect training-nav-btn"
              [attr.aria-label]="
                canGoNext ? t()['train.next'] : t()['train.finish']
              "
              [title]="canGoNext ? t()['train.next'] : t()['train.finish']"
              [disabled]="isReviewBusy"
              (click)="next()"
            >
              <svg class="sa-icon sa-bold sa-nofill">
                <use href="/assets/icons/sprite.svg#arrow-right"></use>
              </svg>
            </button>
          </div>
        </div>
      }
    }
  </div>
</section>
